import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { translateCategory, translateCountry } from '../utils/translations';
import { Header } from '../components/layout/Header';
import ErrorBoundary from '../components/ErrorBoundary';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { CRMTab } from '../components/admin/CRMTab';
import TimeTrackingTab from '../components/admin/TimeTrackingTab';
import ChatbotTab from '../components/admin/ChatbotTab';
import SiteContentTab from '../components/admin/SiteContentTab';
import ExcelCleanerTab from '../components/admin/ExcelCleanerTab';
import InvoiceClassifierTab from '../components/admin/InvoiceClassifierTab';
import PdfFilterTab from '../components/admin/PdfFilterTab';
import ClientCatalogTab from '../components/admin/ClientCatalogTab';
import UrlCheckerTab from '../components/admin/UrlCheckerTab';
import CompareToolTab from '../components/admin/CompareToolTab';
import LinkMonitorTab from '../components/admin/LinkMonitorTab';
import AhrefsBatchTab from '../components/admin/AhrefsBatchTab';
import AhrefsIntegrationTab from '../components/admin/AhrefsIntegrationTab';
import AhrefsUpdateTab from '../components/admin/AhrefsUpdateTab';
import CleanUrlsTab from '../components/admin/CleanUrlsTab';
import ApiToExcelTab from '../components/admin/ApiToExcelTab';
import CatalogApiTab from '../components/admin/CatalogApiTab';
import APISyncTab from '../components/admin/APISyncTab';
import MediaSearchTab from '../components/admin/MediaSearchTab';
import EmailToolTab from '../components/admin/EmailToolTab';
import UsersTab from '../components/admin/UsersTab';
import ProductsTab from '../components/admin/ProductsTab';
import ConsultasTab from '../components/admin/ConsultasTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import CouponsTab from '../components/admin/CouponsTab';
import RemindersTab from '../components/admin/RemindersTab';
import StatsTab from '../components/admin/StatsTab';
import ImportTab from '../components/admin/ImportTab';
import WalletTab from '../components/admin/WalletTab';
import CategoryClassifierTab from '../components/admin/CategoryClassifierTab';
import SEOProspectorTab from '../components/admin/SEOProspectorTab';
import HealthCheckTab from '../components/admin/HealthCheckTab';
import BotTesterTab from '../components/admin/BotTesterTab';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { toast } from 'sonner';
import {
  Users,
  Package,
  BarChart3,
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Loader2,
  RefreshCw,
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Globe,
  FolderOpen,
  Search,
  X,
  History,
  Calendar,
  Filter,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  ShoppingCart,
  Check,
  CreditCard,
  Wallet,
  Building2,
  Clock,
  XCircle,
  Percent,
  Ticket,
  Copy,
  Mail,
  Eye,
  Edit,
  Receipt,
  Minus,
  ClipboardList,
  Bell,
  Settings,
  Play,
  Pause,
  MessageCircle,
  FileText,
  GitCompare,
  Link2,
  Wrench,
  Briefcase,
  Database,
  Scissors,
  Key,
  TrendingUp,
  Tags,
  ShieldCheck,
  Bot,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Product form options
const PRODUCT_COUNTRIES = [
  'AFGHANISTAN', 'ALGERIA', 'ANGOLA', 'ARGENTINA', 'AUSTRALIA', 'AUSTRIA',
  'BANGLADESH', 'BELARUS', 'BELGIUM', 'BELIZE', 'BENIN', 'BOLIVIA',
  'BOSNIA AND HERZEGOVINA', 'BRAZIL', 'BULGARIA', 'BURKINA FASO', 'CAMEROON',
  'CANADA', 'CHILE', 'CHINA', 'COLOMBIA', 'COMOROS', 'CONGO',
  'CONGO, THE DEMOCRATIC REPUBLIC OF THE', 'COSTA RICA', "COTE D'IVOIRE",
  'CROATIA', 'CZECH REPUBLIC', 'DENMARK', 'DOMINICAN REPUBLIC', 'ECUADOR',
  'EGYPT', 'EL SALVADOR', 'ESTONIA', 'ETHIOPIA', 'FINLAND', 'FRANCE',
  'FRENCH GUIANA', 'GABON', 'GERMANY', 'GHANA', 'GLOBAL', 'GREECE',
  'GUATEMALA', 'HONDURAS', 'HONG KONG', 'HUNGARY', 'INDIA', 'INDONESIA',
  'IRELAND', 'ISRAEL', 'ITALY', 'JAPAN', 'JORDAN', 'KENYA',
  "KOREA, DEMOCRATIC PEOPLE'S REPUBLIC OF", 'KOREA, REPUBLIC OF', 'LATVIA',
  'LIBYAN ARAB JAMAHIRIYA', 'LIECHTENSTEIN', 'LITHUANIA', 'LUXEMBOURG',
  'MALAYSIA', 'MALDIVES', 'MALI', 'MALTA', 'MEXICO', 'MOROCCO', 'NEPAL',
  'NETHERLANDS', 'NEW ZEALAND', 'NICARAGUA', 'NIGER', 'NIGERIA', 'NORWAY',
  'PAKISTAN', 'PANAMA', 'PARAGUAY', 'PERU', 'PHILIPPINES', 'POLAND',
  'PORTUGAL', 'PUERTO RICO', 'QATAR', 'ROMANIA', 'RUSSIAN FEDERATION',
  'RWANDA', 'SAUDI ARABIA', 'SENEGAL', 'SINGAPORE', 'SLOVAKIA', 'SLOVENIA',
  'SOUTH AFRICA', 'SPAIN', 'SWEDEN', 'SWITZERLAND',
  'TAIWAN, PROVINCE OF CHINA', 'TANZANIA, UNITED REPUBLIC OF', 'THAILAND',
  'TOGO', 'TRINIDAD AND TOBAGO', 'TURKEY', 'UGANDA', 'UKRAINE',
  'UNITED ARAB EMIRATES', 'UNITED KINGDOM', 'UNITED STATES', 'URUGUAY',
  'VENEZUELA', 'VIET NAM', 'ZAMBIA', 'ZIMBABWE'
].sort();

const PRODUCT_CATEGORIES = [
  'ANIME', 'APPS', 'ART', 'BABIES', 'BEAUTY', 'BUSINESS', 'CANNABIS', 'CARS',
  'CASINOS', 'CATERING', 'CBD', 'CHILDREN', 'CHRISTMAS', 'CINEMA', 'COMPUTERS',
  'CONSTRUCTION', 'COOKING', 'COPYWRITING', 'COUPLES', 'CRYPTOCURRENCY',
  'CYCLING', 'DATING', 'DECORATION', 'DESIGN', 'DIY', 'DRUGS', 'ECOLOGY',
  'ECONOMY', 'EDUCATION', 'EMPLOYMENT', 'ENGINEERING', 'EROTIC', 'ESCORTS',
  'ESOTERICISM', 'FAMILY', 'FASHION', 'FINANCE', 'FITNESS', 'FOOD', 'FOREX',
  'GAMBLING', 'GAME CONSOLES', 'GAMES', 'GAMING', 'GARDEN', 'GASTRONOMY',
  'GENERAL', 'GYM', 'HEALTH', 'HISTORY', 'HOME', 'HOTELS', 'HUMOR',
  'INDUSTRIAL', 'INSURANCE', 'INTERNET', 'KIDS', 'LANGUAGES', 'LEADERSHIP',
  'LEGAL', 'LEISURE', 'LIFESTYLE', 'LOGISTICS', 'LOVE', 'MANGA', 'MARKETING',
  'MATERNITY', 'MEDITATION', 'MOBILE', 'MOTOR', 'MOTORCYCLES', 'MUSIC',
  'NATURE', 'NEWS', 'PERSONAL DEVELOPMENT', 'PETS', 'PHOTO', 'POLITICS',
  'PSYCHIATRY', 'PSYCHOLOGY', 'REAL ESTATE', 'RELATIONSHIPS', 'RELIGION',
  'RENEWABLE ENERGY', 'RRSS', 'SCIENCE', 'SEER', 'SEO', 'SEXUALITY', 'SOCIAL',
  'SOCIETY', 'SPORTS', 'TAROT', 'TECHNOLOGY', 'TOURISM', 'TRANSPORTATION',
  'TRAVEL', 'TUTORIALS', 'TV', 'VPN', 'WEDDINGS', 'YOGA'
].sort();

const PRODUCT_LANGUAGES = [
  'BULGARIAN', 'CATALAN', 'CHINESE', 'CROATIAN', 'CZECH', 'DANISH', 'DUTCH',
  'ENGLISH', 'FINNISH', 'FRENCH', 'GERMAN', 'GREEK', 'HEBREW', 'HUNGARIAN',
  'ITALIAN', 'JAPANESE', 'KOREAN', 'LITHUANIAN', 'NORWEGIAN', 'POLISH',
  'PORTUGUESE', 'PORTUGUESE BR', 'ROMANIAN', 'RUSSIAN', 'SLOVAKIAN', 'SPANISH',
  'SWEDISH', 'SWISS', 'TAIWANESE', 'TURKISH'
].sort();

const LINK_TYPES = ['follow', 'no-follow', 'sponsored', 'follow, no-follow', 'follow, sponsored', 'no-follow, sponsored', 'follow, no-follow, sponsored'];

const LINK_DURATIONS = ['permanente', '1 año', '2 años', '3 años', '4 años'];

const SITE_TYPES = ['Web/Blog', 'Periódicos', 'Redactor', 'Sin categoría'];

const USER_ROLES = [
  { value: 'admin', label: { es: 'Administrador', en: 'Admin' }, color: 'bg-purple-100 text-purple-700' },
  { value: 'worker', label: { es: 'Trabajador', en: 'Worker' }, color: 'bg-blue-100 text-blue-700' },
  { value: 'client', label: { es: 'Cliente', en: 'Client' }, color: 'bg-green-100 text-green-700' },
];

export const AdminPage = () => {
  const { user, isAdmin, isSuperadmin } = useAuth();
  const { language, at, tx } = useAdminTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Get token from localStorage
  const token = localStorage.getItem('auth_token');

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [writers, setWriters] = useState([]);
  const [productEmails, setProductEmails] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [pendingRechargesCount, setPendingRechargesCount] = useState(0);
  const [ordersTabKey, setOrdersTabKey] = useState(0); // Key para forzar remontaje de CRMTab
  
  // Coupons state
  const [coupons, setCoupons] = useState([]);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '',
    max_uses: '',
    expires_at: '',
  });
  
  // Search state for products
  const [productSearch, setProductSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Admin filters state
  const [adminFilters, setAdminFilters] = useState({
    countries: [],  // Changed to array for multi-select
    categories: [], // Changed to array for multi-select
    drMin: '',
    drMax: '',
    trafficMin: '',
    trafficMax: '',
    priceMin: '',
    priceMax: '',
    email: '', // Email search filter
  });
  const [filterOptions, setFilterOptions] = useState({ countries: [], categories: [] });
  
  // Delete confirmation dialog
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Product selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [exportingSelected, setExportingSelected] = useState(false);

  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Changes export state
  const [changeDays, setChangeDays] = useState(1);
  const [exportingChanges, setExportingChanges] = useState(false);

  // Wallet recharges state
  const [recharges, setRecharges] = useState([]);
  const [rechargesLoading, setRechargesLoading] = useState(false);
  const [rechargeFilter, setRechargeFilter] = useState('pending');

  // Custom categories state
  const [customCategories, setCustomCategories] = useState([...PRODUCT_CATEGORIES]);
  const [categorySearch, setCategorySearch] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  // User form state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Wallet adjustment dialog state
  const [walletAdjustDialog, setWalletAdjustDialog] = useState(false);
  const [walletAdjustUser, setWalletAdjustUser] = useState(null);
  const [walletAdjustAmount, setWalletAdjustAmount] = useState('');
  const [walletAdjustReason, setWalletAdjustReason] = useState('');
  const [walletAdjustType, setWalletAdjustType] = useState('add');
  const [adjustingWallet, setAdjustingWallet] = useState(false);
  
  // User import state
  const [importingUsers, setImportingUsers] = useState(false);
  const [showUserImportDialog, setShowUserImportDialog] = useState(false);
  const [userImportResult, setUserImportResult] = useState(null);
  const userImportInputRef = React.useRef(null);

  // Sensitive categories state
  const [sensitiveCategories, setSensitiveCategories] = useState([]);
  const [newSensitiveCategoryName, setNewSensitiveCategoryName] = useState('');

  // Reminders state
  const [reminderConfig, setReminderConfig] = useState({
    consultando_hours: 48,
    revision_hours: 72,
    consulta_hours: 72,
    scheduler_enabled: true
  });
  const [reminderStatus, setReminderStatus] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [reminderHistoryStats, setReminderHistoryStats] = useState(null);
  const [reminderHistoryFilter, setReminderHistoryFilter] = useState('');
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [savingReminderConfig, setSavingReminderConfig] = useState(false);

  // Consultas state
  const [consultas, setConsultas] = useState([]);
  const [consultaDialogOpen, setConsultaDialogOpen] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [consultaForm, setConsultaForm] = useState({
    worker_id: '',
    worker_name: '',
    worker_email: '',
    client_id: '',
    client_name: '',
    client_email: '',
    editor_id: '',
    editor_name: '',
    editor_email: '',
    start_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
  });
  const [consultaStatusFilter, setConsultaStatusFilter] = useState('');
  const [consultaSearch, setConsultaSearch] = useState('');

  // Product form state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    website: '',
    country: '',
    category: '',
    casino: false,
    cbd: false,
    gambling: false,
    cryptocurrency: false,
    sex: false,
    erotic: false,
    vpn: false,
    tarot: false,
    porn: false,
    cannabis: false,
    tag_label: '',
    ad_label: '',
    price: 0,
    price_sensitive: 0,
    pvp: 0,
    pc: 0,
    premium_sale_price: 0,
    premium_purchase_price: 0,
    mas_info_pc: '',
    more_info: '',
    available: true,
    motivo_eliminacion: '',
    dr: 0,
    organic_traffic: 0,
    language: '',
    site_type: '',
    link_type: 'Dofollow',
    link_duration: 'Permanente',
    web_email: '',
    second_email: '',
  });

  const t = {
    es: {
      title: 'Panel de Administración',
      stats: 'Estadísticas',
      users: 'Usuarios',
      products: 'Productos',
      import: 'Importar',
      export: 'Exportar',
      totalUsers: 'Total Usuarios',
      adminUsers: 'Administradores',
      totalProducts: 'Total Productos',
      activeProducts: 'Productos Activos',
      availableProducts: 'Disponibles',
      uniqueCountries: 'Países',
      uniqueCategories: 'Categorías',
      name: 'Nombre',
      email: 'Email',
      role: 'Rol',
      status: 'Estado',
      actions: 'Acciones',
      admin: 'Admin',
      user: 'Usuario',
      active: 'Activo',
      inactive: 'Inactivo',
      makeAdmin: 'Hacer Admin',
      removeAdmin: 'Quitar Admin',
      activate: 'Activar',
      deactivate: 'Desactivar',
      delete: 'Eliminar',
      deleteAll: 'Eliminar Todo',
      addProduct: 'Añadir Producto',
      editProduct: 'Editar Producto',
      website: 'Website',
      country: 'País',
      category: 'Categoría',
      price: 'Precio Venta',
      priceSensitive: 'Precio Sensible',
      dr: 'DR',
      traffic: 'Tráfico',
      adLabel: 'Etiqueta',
      moreInfo: 'Más Info',
      available: 'Disponible',
      languageField: 'Idioma',
      siteType: 'Tipo Sitio',
      linkType: 'Tipo Enlace',
      linkDuration: 'Duración',
      save: 'Guardar',
      cancel: 'Cancelar',
      confirmDelete: '¿Estás seguro de que quieres eliminar este elemento?',
      confirmDeleteAll: '¿Estás seguro de que quieres eliminar TODOS los productos? Esta acción no se puede deshacer.',
      importTitle: 'Importador de Productos',
      importDescription: 'Sube un archivo Excel (.xlsx) con los productos a importar',
      selectFile: 'Seleccionar Archivo',
      replaceAll: 'Reemplazar todos los productos existentes',
      replaceAllWarning: 'Esta opción eliminará todos los productos actuales antes de importar',
      downloadTemplate: 'Descargar Plantilla',
      importButton: 'Importar',
      importing: 'Importando...',
      importSuccess: 'Importación completada',
      importedRows: 'productos importados',
      errorRows: 'errores',
      exportAll: 'Exportar Todo',
      exportFiltered: 'Exportar Filtrados',
      exportPage: 'Exportar Página',
      page: 'Página',
      of: 'de',
      showing: 'Mostrando',
      noProducts: 'No hay productos',
      searchPlaceholder: 'Buscar por dominio...',
      search: 'Buscar',
      clearSearch: 'Limpiar',
      exportChanges: 'Descargar Cambios',
      exportingChanges: 'Descargando...',
      days: 'días',
      today: 'Hoy',
      lastDays: 'Últimos',
      filterByCountry: 'Filtrar por país',
      filterByCategory: 'Filtrar por categoría',
      allCountries: 'Todos los países',
      allCategories: 'Todas las categorías',
      drMin: 'DR mín',
      drMax: 'DR máx',
      trafficMin: 'Tráfico mín',
      trafficMax: 'Tráfico máx',
      priceMin: 'Precio mín',
      priceMax: 'Precio máx',
      confirmDeleteAllTitle: '¿Eliminar TODOS los productos?',
      confirmDeleteAllDesc: 'Esta acción no se puede deshacer. Se eliminarán todos los productos de la base de datos.',
      typeToConfirm: 'Escribe ELIMINAR para confirmar:',
      deleteWord: 'ELIMINAR',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      selected: 'seleccionados',
      exportSelected: 'Descargar Seleccionados',
      noSelection: 'No hay productos seleccionados',
      addUser: 'Añadir Usuario',
      createUser: 'Crear Usuario',
      password: 'Contraseña',
      isAdmin: 'Es Administrador',
      userCreated: 'Usuario creado correctamente',
      userCreateError: 'Error al crear usuario',
    },
    en: {
      title: 'Admin Panel',
      stats: 'Statistics',
      users: 'Users',
      products: 'Products',
      import: 'Import',
      export: 'Export',
      totalUsers: 'Total Users',
      adminUsers: 'Administrators',
      totalProducts: 'Total Products',
      activeProducts: 'Active Products',
      availableProducts: 'Available',
      uniqueCountries: 'Countries',
      uniqueCategories: 'Categories',
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      actions: 'Actions',
      admin: 'Admin',
      user: 'User',
      active: 'Active',
      inactive: 'Inactive',
      makeAdmin: 'Make Admin',
      removeAdmin: 'Remove Admin',
      activate: 'Activate',
      deactivate: 'Deactivate',
      delete: 'Delete',
      deleteAll: 'Delete All',
      addProduct: 'Add Product',
      editProduct: 'Edit Product',
      website: 'Website',
      country: 'Country',
      category: 'Category',
      price: 'Sale Price',
      priceSensitive: 'Sensitive Price',
      dr: 'DR',
      traffic: 'Traffic',
      adLabel: 'Label',
      moreInfo: 'More Info',
      available: 'Available',
      languageField: 'Language',
      siteType: 'Site Type',
      linkType: 'Link Type',
      linkDuration: 'Duration',
      save: 'Save',
      cancel: 'Cancel',
      confirmDelete: 'Are you sure you want to delete this item?',
      confirmDeleteAll: 'Are you sure you want to delete ALL products? This action cannot be undone.',
      importTitle: 'Product Importer',
      importDescription: 'Upload an Excel file (.xlsx) with the products to import',
      selectFile: 'Select File',
      replaceAll: 'Replace all existing products',
      replaceAllWarning: 'This option will delete all current products before importing',
      downloadTemplate: 'Download Template',
      importButton: 'Import',
      importing: 'Importing...',
      importSuccess: 'Import completed',
      importedRows: 'products imported',
      errorRows: 'errors',
      exportAll: 'Export All',
      exportFiltered: 'Export Filtered',
      exportPage: 'Export Page',
      page: 'Page',
      of: 'of',
      showing: 'Showing',
      noProducts: 'No products',
      searchPlaceholder: 'Search by domain...',
      search: 'Search',
      clearSearch: 'Clear',
      exportChanges: 'Download Changes',
      exportingChanges: 'Downloading...',
      days: 'days',
      today: 'Today',
      lastDays: 'Last',
      filterByCountry: 'Filter by country',
      filterByCategory: 'Filter by category',
      allCountries: 'All countries',
      allCategories: 'All categories',
      drMin: 'DR min',
      drMax: 'DR max',
      trafficMin: 'Traffic min',
      trafficMax: 'Traffic max',
      priceMin: 'Price min',
      priceMax: 'Price max',
      confirmDeleteAllTitle: 'Delete ALL products?',
      confirmDeleteAllDesc: 'This action cannot be undone. All products will be deleted from the database.',
      typeToConfirm: 'Type DELETE to confirm:',
      deleteWord: 'DELETE',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      selected: 'selected',
      exportSelected: 'Download Selected',
      noSelection: 'No products selected',
      addUser: 'Add User',
      createUser: 'Create User',
      password: 'Password',
      isAdmin: 'Is Administrator',
      userCreated: 'User created successfully',
      userCreateError: 'Error creating user',
    },
  };

  const texts = t[language] || t.es;

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  // Fetch recharges when wallet tab is active
  useEffect(() => {
    if (activeTab === 'wallet') {
      fetchRecharges(rechargeFilter);
    }
    if (activeTab === 'coupons') {
      fetchCoupons();
    }
    if (activeTab === 'categories') {
      fetchSensitiveCategories();
    }
  }, [activeTab, rechargeFilter]);

  // Listen for close email tool event
  useEffect(() => {
    const handler = () => setActiveTab('stats');
    window.addEventListener('closeEmailTool', handler);
    return () => window.removeEventListener('closeEmailTool', handler);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, pendingRes, pendingRechargesRes, consultasRes, writersRes, productEmailsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/orders/pending-count`),
        axios.get(`${API}/admin/wallet/recharges/pending-count`),
        axios.get(`${API}/admin/consultas`),
        axios.get(`${API}/admin/writers`),
        axios.get(`${API}/admin/products/emails`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingOrdersCount(pendingRes.data.count || 0);
      setPendingRechargesCount(pendingRechargesRes.data.count || 0);
      setConsultas(consultasRes.data || []);
      setWriters(writersRes.data?.writers || writersRes.data || []);
      setProductEmails(productEmailsRes.data?.emails || []);
      await fetchProducts(1);
      await fetchFilterOptions();
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPendingOrdersCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/pending-count`);
      setPendingOrdersCount(res.data.count || 0);
    } catch (error) {
      console.error('Error fetching pending orders count:', error);
    }
  };

  const fetchPendingRechargesCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/wallet/recharges/pending-count`);
      setPendingRechargesCount(res.data.count || 0);
    } catch (error) {
      console.error('Error fetching pending recharges count:', error);
    }
  };

  const fetchSensitiveCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/sensitive-categories`);
      setSensitiveCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching sensitive categories:', error);
    }
  };

  const handleAddSensitiveCategory = async () => {
    const category = newSensitiveCategoryName.trim().toUpperCase();
    if (!category) return;
    
    try {
      const res = await axios.post(`${API}/admin/sensitive-categories`, { category });
      setSensitiveCategories(res.data.categories || []);
      setNewSensitiveCategoryName('');
      toast.success(tx(`Categoría "${category}" añadida`, `Category "${category}" added`));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding category');
    }
  };

  const handleDeleteSensitiveCategory = async (category) => {
    if (!confirm(tx(`¿Eliminar la categoría sensible "${category}"?`, `Delete sensitive category "${category}"?`))) {
      return;
    }
    
    try {
      const res = await axios.delete(`${API}/admin/sensitive-categories/${encodeURIComponent(category)}`);
      setSensitiveCategories(res.data.categories || []);
      toast.success(tx(`Categoría "${category}" eliminada`, `Category "${category}" deleted`));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting category');
    }
  };

  const fetchProducts = async (page = 1, search = productSearch, filters = adminFilters, sort = sortField, order = sortOrder) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (search) params.append('search', search);
      // Handle multiple countries
      if (filters.countries && filters.countries.length > 0) {
        params.append('countries', filters.countries.join(','));
      }
      // Handle multiple categories
      if (filters.categories && filters.categories.length > 0) {
        params.append('categories', filters.categories.join(','));
      }
      if (filters.drMin) params.append('dr_min', filters.drMin);
      if (filters.drMax) params.append('dr_max', filters.drMax);
      if (filters.trafficMin) params.append('traffic_min', filters.trafficMin);
      if (filters.trafficMax) params.append('traffic_max', filters.trafficMax);
      if (filters.priceMin) params.append('price_min', filters.priceMin);
      if (filters.priceMax) params.append('price_max', filters.priceMax);
      // Email filter
      if (filters.email) params.append('email', filters.email);
      if (sort) {
        params.append('sort_by', sort);
        params.append('sort_order', order);
      }
      
      const response = await axios.get(`${API}/admin/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };
  
  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(newOrder);
    fetchProducts(1, productSearch, adminFilters, field, newOrder);
  };

  // Wallet Recharge Functions
  const fetchRecharges = async (status = rechargeFilter) => {
    setRechargesLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.append('status', status);
      params.append('limit', 100);
      
      const response = await axios.get(`${API}/admin/wallet/recharges?${params.toString()}`);
      setRecharges(response.data.recharges || []);
    } catch (error) {
      console.error('Error fetching recharges:', error);
      toast.error('Error al cargar recargas');
    } finally {
      setRechargesLoading(false);
    }
  };

  const handleApproveRecharge = async (rechargeId) => {
    try {
      await axios.post(`${API}/admin/wallet/recharges/${rechargeId}/approve`);
      toast.success('Recarga aprobada correctamente');
      fetchRecharges(rechargeFilter);
      fetchPendingRechargesCount();
    } catch (error) {
      console.error('Error approving recharge:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar recarga');
    }
  };

  const handleRejectRecharge = async (rechargeId) => {
    const reason = prompt('Motivo del rechazo:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/admin/wallet/recharges/${rechargeId}/reject`, { reason });
      toast.success('Recarga rechazada');
      fetchRecharges(rechargeFilter);
      fetchPendingRechargesCount();
    } catch (error) {
      console.error('Error rejecting recharge:', error);
      toast.error(error.response?.data?.detail || 'Error al rechazar recarga');
    }
  };
  
  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters/options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };
  
  const handleProductSearch = (e) => {
    e.preventDefault();
    fetchProducts(1, productSearch, adminFilters);
  };
  
  const handleClearSearch = () => {
    setProductSearch('');
    setAdminFilters({ countries: [], categories: [], drMin: '', drMax: '', trafficMin: '', trafficMax: '', priceMin: '', priceMax: '', email: '' });
    fetchProducts(1, '', { countries: [], categories: [], drMin: '', drMax: '', trafficMin: '', trafficMax: '', priceMin: '', priceMax: '', email: '' });
  };
  
  const handleAdminFilterChange = (key, value) => {
    const newFilters = { ...adminFilters, [key]: value };
    setAdminFilters(newFilters);
    fetchProducts(1, productSearch, newFilters);
  };
  
  // Toggle functions for multi-select filters
  const handleCountryToggle = (country) => {
    const currentCountries = adminFilters.countries || [];
    const newCountries = currentCountries.includes(country)
      ? currentCountries.filter(c => c !== country)
      : [...currentCountries, country];
    handleAdminFilterChange('countries', newCountries);
  };
  
  const handleCategoryToggle = (category) => {
    const currentCategories = adminFilters.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    handleAdminFilterChange('categories', newCategories);
  };

  const handleUserUpdate = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating user');
    }
  };

  const handleWalletAdjustment = async () => {
    if (!walletAdjustUser || !walletAdjustAmount || !walletAdjustReason.trim()) {
      toast.error(tx('Completa todos los campos', 'Fill all fields'));
      return;
    }
    
    const amount = parseFloat(walletAdjustAmount);
    if (amount <= 0) {
      toast.error(tx('El importe debe ser mayor que 0', 'Amount must be greater than 0'));
      return;
    }
    
    setAdjustingWallet(true);
    try {
      await axios.post(`${API}/admin/users/${walletAdjustUser.id}/wallet-adjustment`, {
        amount: amount,
        type: walletAdjustType,
        reason: walletAdjustReason.trim()
      });
      
      const sign = walletAdjustType === 'add' ? '+' : '-';
      toast.success(tx(
        `${sign}€${amount.toFixed(2)} - Notificación enviada al cliente`,
        `${sign}€${amount.toFixed(2)} - Notification sent to customer`
      ));
      
      // Reset and close
      setWalletAdjustDialog(false);
      setWalletAdjustUser(null);
      setWalletAdjustAmount('');
      setWalletAdjustReason('');
      setWalletAdjustType('add');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adjusting wallet');
    } finally {
      setAdjustingWallet(false);
    }
  };

  const handleUserDelete = async (userId) => {
    if (!window.confirm(texts.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  // Impersonate user (admin only)
  const handleImpersonateUser = async (userId, userEmail) => {
    if (!window.confirm(tx(
      `¿Quieres suplantar al usuario ${userEmail}? Se abrirá una nueva pestaña con su sesión.`,
      `Do you want to impersonate user ${userEmail}? A new tab will open with their session.`
    ))) return;
    
    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`);
      const { access_token, user: impersonatedUser } = response.data;
      
      // Store impersonation data in sessionStorage (not localStorage) for security
      const impersonationData = {
        token: access_token,
        user: impersonatedUser,
        originalAdmin: user?.email
      };
      
      // Open new tab with impersonation
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.location.href = `${window.location.origin}/?impersonate=${encodeURIComponent(JSON.stringify(impersonationData))}`;
      }
      
      toast.success(tx(
        `Suplantando a ${userEmail}`,
        `Impersonating ${userEmail}`
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error al suplantar usuario', 'Error impersonating user'));
    }
  };

  // Reminders handlers
  const fetchReminderData = async () => {
    setLoadingReminders(true);
    try {
      const [configRes, statusRes, historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reminders/config`),
        axios.get(`${API}/admin/reminders/status`),
        axios.get(`${API}/admin/reminders/history?limit=50`),
        axios.get(`${API}/admin/reminders/history/stats`)
      ]);
      setReminderConfig(configRes.data);
      setReminderStatus(statusRes.data);
      setReminderHistory(historyRes.data.history || []);
      setReminderHistoryStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reminder data:', error);
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleSaveReminderConfig = async () => {
    setSavingReminderConfig(true);
    try {
      await axios.put(`${API}/admin/reminders/config`, {
        consultando_hours: parseInt(reminderConfig.consultando_hours),
        revision_hours: parseInt(reminderConfig.revision_hours),
        consulta_hours: parseInt(reminderConfig.consulta_hours),
        scheduler_enabled: reminderConfig.scheduler_enabled
      });
      toast.success(tx('Configuración guardada', 'Configuration saved'));
      fetchReminderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving config');
    } finally {
      setSavingReminderConfig(false);
    }
  };

  const handleRunReminders = async () => {
    setLoadingReminders(true);
    try {
      const res = await axios.post(`${API}/admin/reminders/check-all`);
      const total = res.data.details.consultando_reminders_sent + 
                    res.data.details.revision_reminders_sent + 
                    res.data.details.consulta_reminders_sent;
      toast.success(tx(
        `Recordatorios enviados: ${total}`,
        `Reminders sent: ${total}`
      ));
      fetchReminderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error running reminders');
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleClearHistory = async (days = 30) => {
    if (!window.confirm(tx(
      `¿Eliminar registros de más de ${days} días?`,
      `Delete records older than ${days} days?`
    ))) return;
    try {
      const res = await axios.delete(`${API}/admin/reminders/history?older_than_days=${days}`);
      toast.success(res.data.message);
      fetchReminderData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error clearing history');
    }
  };

  // Consultas handlers
  const handleCreateConsulta = async () => {
    try {
      await axios.post(`${API}/admin/consultas`, consultaForm);
      toast.success(tx('Consulta creada', 'Query created'));
      setConsultaDialogOpen(false);
      setConsultaForm({
        worker_id: '',
        worker_name: '',
        worker_email: '',
        client_id: '',
        client_name: '',
        client_email: '',
        editor_id: '',
        editor_name: '',
        editor_email: '',
        start_date: new Date().toISOString().split('T')[0],
        description: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating consulta');
    }
  };

  const handleUpdateConsulta = async (consultaId, updates) => {
    try {
      await axios.put(`${API}/admin/consultas/${consultaId}`, updates);
      toast.success(tx('Consulta actualizada', 'Query updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating consulta');
    }
  };

  const handleFinalizeConsulta = async (consultaId) => {
    try {
      await axios.post(`${API}/admin/consultas/${consultaId}/finalize`);
      toast.success(tx('Consulta finalizada', 'Query finalized'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error finalizing consulta');
    }
  };

  const handleDeleteConsulta = async (consultaId) => {
    if (!window.confirm(tx('¿Eliminar esta consulta?', 'Delete this query?'))) return;
    try {
      await axios.delete(`${API}/admin/consultas/${consultaId}`);
      toast.success(tx('Consulta eliminada', 'Query deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting consulta');
    }
  };

  // Get workers (users with role worker or admin)
  const getWorkers = () => {
    return users.filter(u => u.role === 'admin' || u.role === 'worker');
  };

  // Get clients
  const getClients = () => {
    return users.filter(u => u.role === 'client');
  };

  // Import users from Excel
  const handleImportUsers = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingUsers(true);
    setUserImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/users/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUserImportResult(response.data);
      setShowUserImportDialog(true);
      toast.success(tx('Importación completada', 'Import completed'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Error en la importación', 'Import error')));
    } finally {
      setImportingUsers(false);
      if (userImportInputRef.current) {
        userImportInputRef.current.value = '';
      }
    }
  };

  const handleDownloadUsersTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_usuarios.xlsx';
      link.click();
    } catch (error) {
      toast.error(tx('Error descargando plantilla', 'Error downloading template'));
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, userForm);
      toast.success(texts.userCreated);
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors which return an array of error objects
      const errorData = error.response?.data;
      let errorMessage = texts.userCreateError;
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setCreatingUser(false);
    }
  };

  // Coupon functions
  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`);
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const handleSaveCoupon = async () => {
    if (!couponForm.code || !couponForm.discount_value) {
      toast.error(tx('Código y valor de descuento son obligatorios', 'Code and discount value are required'));
      return;
    }
    
    try {
      const data = {
        code: couponForm.code,
        description: couponForm.description,
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        min_purchase: parseFloat(couponForm.min_purchase) || 0,
        max_uses: parseInt(couponForm.max_uses) || 0,
        expires_at: couponForm.expires_at || null,
      };
      
      if (editingCoupon) {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, data);
        toast.success(tx('Cupón actualizado', 'Coupon updated'));
      } else {
        await axios.post(`${API}/admin/coupons`, data);
        toast.success(tx('Cupón creado', 'Coupon created'));
      }
      
      setCouponDialogOpen(false);
      setEditingCoupon(null);
      setCouponForm({ code: '', description: '', discount_type: 'percentage', discount_value: '', min_purchase: '', max_uses: '', expires_at: '' });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm(tx('¿Eliminar este cupón?', 'Delete this coupon?'))) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success(tx('Cupón eliminado', 'Coupon deleted'));
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting coupon');
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const response = await axios.patch(`${API}/admin/coupons/${couponId}/toggle`);
      toast.success(response.data.is_active ? 'Cupón activado' : 'Cupón desactivado');
      fetchCoupons();
    } catch (error) {
      toast.error('Error toggling coupon');
    }
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: String(coupon.discount_value),
      min_purchase: String(coupon.min_purchase || ''),
      max_uses: String(coupon.max_uses || ''),
      expires_at: coupon.expires_at || '',
    });
    setCouponDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(tx('Copiado al portapapeles', 'Copied to clipboard'));
  };

  const handleProductSave = async () => {
    // Validación de campos obligatorios
    const requiredFields = [
      { field: 'website', name: tx('URL del sitio', 'Website URL') },
      { field: 'country', name: tx('País', 'Country') },
      { field: 'category', name: tx('Categoría', 'Category') },
      { field: 'price', name: tx('Precio de venta (PVP)', 'Sale price (PVP)') },
      { field: 'pc', name: tx('Precio de compra (PC)', 'Purchase price (PC)') },
      { field: 'link_type', name: tx('Tipo de enlace', 'Link type') },
      { field: 'link_duration', name: tx('Duración del enlace', 'Link duration') },
      { field: 'web_email', name: tx('Email del medio', 'Media email') },
    ];
    
    const missingFields = requiredFields.filter(({ field }) => {
      const value = productForm[field];
      if (typeof value === 'number') return value <= 0;
      return !value || value.trim() === '';
    });
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(f => f.name).join(', ');
      toast.error(tx(
        `Completa los campos obligatorios: ${fieldNames}`,
        `Complete required fields: ${fieldNames}`
      ));
      return;
    }
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productForm);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, productForm);
        toast.success('Product created');
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      fetchProducts(pagination.page);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving product');
    }
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm(texts.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts(pagination.page);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting product');
    }
  };

  const handleDeleteAllProducts = async () => {
    if (deleteConfirmText !== 'ELIMINAR') {
      toast.error(tx('Escribe ELIMINAR para confirmar', 'Type DELETE to confirm'));
      return;
    }
    try {
      const response = await axios.delete(`${API}/admin/products`);
      toast.success(response.data.message);
      setDeleteAllDialogOpen(false);
      setDeleteConfirmText('');
      fetchProducts(1);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting products');
    }
  };

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        website: product.website || '',
        country: product.country || '',
        category: product.category || '',
        casino: product.casino || false,
        cbd: product.cbd || false,
        gambling: product.gambling || false,
        cryptocurrency: product.cryptocurrency || false,
        sex: product.sex || false,
        erotic: product.erotic || false,
        vpn: product.vpn || false,
        tarot: product.tarot || false,
        porn: product.porn || false,
        cannabis: product.cannabis || false,
        tag_label: product.tag_label || '',
        ad_label: product.ad_label || '',
        price: product.price || product.pvp || 0,
        price_sensitive: product.premium_price || product.premium_sale_price || product.price_sensitive || 0,
        pvp: product.pvp || product.price || 0,
        pc: product.pc || 0,
        premium_sale_price: product.premium_price || product.premium_sale_price || product.price_sensitive || 0,
        premium_purchase_price: product.premium_purchase_price || product.precio_compra || 0,
        mas_info_pc: product.mas_info_pc || '',
        more_info: product.more_info || '',
        available: product.available !== false,
        motivo_eliminacion: product.motivo_eliminacion || '',
        dr: product.dr || 0,
        organic_traffic: product.organic_traffic || 0,
        language: product.language || '',
        site_type: product.site_type || '',
        link_type: product.link_type || '',
        link_duration: product.link_duration || '',
        web_email: product.web_email || '',
        second_email: product.second_email || '',
      });
    } else {
      setEditingProduct(null);
      resetProductForm();
    }
    setProductDialogOpen(true);
  };

  const resetProductForm = () => {
    setProductForm({
      website: '',
      country: '',
      category: '',
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      tag_label: '',
      ad_label: '',
      price: 0,
      price_sensitive: 0,
      pvp: 0,
      pc: 0,
      premium_sale_price: 0,
      premium_purchase_price: 0,
      mas_info_pc: '',
      more_info: '',
      available: true,
      motivo_eliminacion: '',
      dr: 0,
      organic_traffic: 0,
      language: '',
      site_type: 'Web/Blog',
      link_type: 'Dofollow',
      link_duration: 'Permanente',
      web_email: '',
      second_email: '',
    });
  };

  // Import/Export functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `${API}/admin/products/import?replace_all=${replaceAll}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setImportResult(response.data);
      toast.success(`${response.data.imported} ${texts.importedRows}`);
      fetchProducts(1);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importing file');
      setImportResult({
        total_rows: 0,
        imported: 0,
        errors: 1,
        error_details: [error.response?.data?.detail || 'Unknown error'],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (exportAll = true, exportFiltered = false) => {
    try {
      const params = new URLSearchParams();
      params.append('format', 'xlsx');
      
      if (exportFiltered) {
        // Export with current filters
        if (productSearch) params.append('search', productSearch);
        // Handle multiple countries
        if (adminFilters.countries && adminFilters.countries.length > 0) {
          params.append('countries', adminFilters.countries.join(','));
        }
        // Handle multiple categories
        if (adminFilters.categories && adminFilters.categories.length > 0) {
          params.append('categories', adminFilters.categories.join(','));
        }
        if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
        if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
        if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
        if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
        if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
        if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
      } else if (!exportAll) {
        // Export current page only
        params.append('page', pagination.page);
        params.append('limit', 50);
      }

      const response = await axios.get(`${API}/admin/products/export?${params.toString()}`, { responseType: 'blob', timeout: 120000 });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `productos_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();

      toast.success('Export completed');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar productos');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/template`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_productos.xlsx';
      link.click();
    } catch (error) {
      toast.error('Error downloading template');
    }
  };

  const handleExportChanges = async () => {
    setExportingChanges(true);
    try {
      const response = await axios.get(`${API}/admin/products/changes/export?days=${changeDays}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `cambios_productos_${changeDays}_dias.xlsx`;
      link.click();
      toast.success(tx('Cambios descargados', 'Changes downloaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error exporting changes');
    } finally {
      setExportingChanges(false);
    }
  };

  // Product selection functions
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Select ALL products from database (not just current page)
  const handleSelectAllFromDB = async () => {
    try {
      // Build params with current filters
      const params = new URLSearchParams();
      if (productSearch) params.append('search', productSearch);
      // Handle multiple countries
      if (adminFilters.countries && adminFilters.countries.length > 0) {
        params.append('countries', adminFilters.countries.join(','));
      }
      // Handle multiple categories
      if (adminFilters.categories && adminFilters.categories.length > 0) {
        params.append('categories', adminFilters.categories.join(','));
      }
      if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
      if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
      if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
      if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
      if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
      if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
      
      const response = await axios.get(`${API}/admin/products/all-ids?${params.toString()}`);
      const allIds = response.data.ids || [];
      setSelectedProducts(new Set(allIds));
      toast.success(tx(`${allIds.length} productos seleccionados`, `${allIds.length} products selected`));
    } catch (error) {
      toast.error(tx('Error al seleccionar todos', 'Error selecting all'));
    }
  };

  const handleExportSelected = async () => {
    if (selectedProducts.size === 0) {
      toast.error(texts.noSelection);
      return;
    }
    
    // Warn if exporting too many products (over 1000)
    if (selectedProducts.size > 1000) {
      const confirmed = confirm(tx(
        `Vas a exportar ${selectedProducts.size.toLocaleString()} productos. Esto puede tardar. ¿Continuar?`,
        `You are about to export ${selectedProducts.size.toLocaleString()} products. This may take a while. Continue?`
      ));
      if (!confirmed) return;
    }
    
    setExportingSelected(true);
    try {
      const productIds = Array.from(selectedProducts);
      console.log(`Exporting ${productIds.length} selected products`);
      
      const response = await axios.post(
        `${API}/admin/products/export-selected`,
        productIds,
        { 
          responseType: 'blob',
          timeout: 120000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `productos_seleccionados_${selectedProducts.size}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(tx(
        `${selectedProducts.size} productos exportados`,
        `${selectedProducts.size} products exported`
      ));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Error exporting selected products');
    } finally {
      setExportingSelected(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-display font-bold text-foreground">{texts.title}</h1>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Sección GESTOR */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Briefcase className="h-3 w-3" />
              Gestor
            </h3>
            <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl">
              <Button
                variant={activeTab === 'stats' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('stats')}
                className={activeTab === 'stats' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-violet-600 hover:bg-white font-medium'}
              >
                <BarChart3 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{texts.stats}</span>
              </Button>
              <Button
                variant={activeTab === 'orders' ? 'default' : 'ghost'}
                onClick={() => { setActiveTab('orders'); setOrdersTabKey(k => k + 1); fetchPendingOrdersCount(); }}
                className={`relative ${activeTab === 'orders' ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-rose-600 hover:bg-white font-medium'}`}
              >
                <ShoppingCart className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Pedidos', 'Orders')}</span>
                {pendingOrdersCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                  </span>
                )}
              </Button>
              <Button
                variant={activeTab === 'products' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('products')}
                className={activeTab === 'products' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-emerald-600 hover:bg-white font-medium'}
              >
                <Package className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{texts.products}</span>
              </Button>
              <Button
                variant={activeTab === 'categories' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('categories')}
                className={activeTab === 'categories' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}
              >
                <Ticket className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Categorías', 'Categories')}</span>
              </Button>
              <Button
                variant={activeTab === 'writers' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('writers')}
                className={activeTab === 'writers' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-amber-600 hover:bg-white font-medium'}
              >
                <Edit className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Redactores', 'Writers')}</span>
              </Button>
              <Button
                variant={activeTab === 'coupons' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('coupons')}
                className={`${activeTab === 'coupons' ? 'bg-pink-600 hover:bg-pink-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-pink-600 hover:bg-white font-medium'}`}
              >
                <Percent className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Cupones', 'Coupons')}</span>
              </Button>
              <Button
                variant={activeTab === 'users' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('users')}
                className={activeTab === 'users' ? 'bg-sky-600 hover:bg-sky-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-sky-600 hover:bg-white font-medium'}
              >
                <Users className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{texts.users}</span>
              </Button>
              <Button
                variant={activeTab === 'consultas' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('consultas')}
                className={activeTab === 'consultas' ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-orange-600 hover:bg-white font-medium'}
              >
                <ClipboardList className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Consultas', 'Queries')}</span>
              </Button>
              <Button
                variant={activeTab === 'clientcatalog' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('clientcatalog')}
                className={`${activeTab === 'clientcatalog' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-blue-600 hover:bg-white font-medium'}`}
                data-testid="client-catalog-tab-btn"
              >
                <Eye className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Catálogo Cliente', 'Client Catalog')}</span>
              </Button>
              <Button
                variant={activeTab === 'wallet' ? 'default' : 'ghost'}
                onClick={() => { setActiveTab('wallet'); fetchPendingRechargesCount(); }}
                className={`relative ${activeTab === 'wallet' ? 'bg-green-600 hover:bg-green-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-green-600 hover:bg-white font-medium'}`}
              >
                <CreditCard className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Recargas</span>
                {pendingRechargesCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingRechargesCount > 99 ? '99+' : pendingRechargesCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Sección HERRAMIENTAS */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Wrench className="h-3 w-3" />
              Herramientas
            </h3>
            <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl">
              <Button
                variant={activeTab === 'urlchecker' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('urlchecker')}
                className={`${activeTab === 'urlchecker' ? 'bg-green-600 hover:bg-green-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-green-600 hover:bg-white font-medium'}`}
                data-testid="url-checker-tab-btn"
              >
                <Globe className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Verificar URLs', 'Check URLs')}</span>
              </Button>
              <Button
                variant={activeTab === 'excelcleaner' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('excelcleaner')}
                className={`${activeTab === 'excelcleaner' ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-cyan-600 hover:bg-white font-medium'}`}
                data-testid="excel-cleaner-tab-btn"
              >
                <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Limpiar Excel', 'Clean Excel')}</span>
              </Button>
              <Button
                variant={activeTab === 'invoiceclassifier' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('invoiceclassifier')}
                className={`${activeTab === 'invoiceclassifier' ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-rose-600 hover:bg-white font-medium'}`}
                data-testid="invoice-classifier-tab-btn"
              >
                <FileText className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Clasificar Facturas', 'Classify Invoices')}</span>
              </Button>
              <Button
                variant={activeTab === 'pdffilter' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('pdffilter')}
                className={`${activeTab === 'pdffilter' ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-orange-600 hover:bg-white font-medium'}`}
                data-testid="pdf-filter-tab-btn"
              >
                <Filter className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Filtrar PDFs', 'Filter PDFs')}</span>
              </Button>
              <Button
                variant={activeTab === 'import' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('import')}
                className={activeTab === 'import' ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-amber-600 hover:bg-white font-medium'}
              >
                <Upload className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{texts.import}</span>
              </Button>
              <Button
                variant={activeTab === 'comparetool' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('comparetool')}
                className={`${activeTab === 'comparetool' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}`}
                data-testid="compare-tool-tab-btn"
              >
                <GitCompare className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Comparar Archivos', 'Compare Files')}</span>
              </Button>
              <Button
                variant={activeTab === 'linkmonitor' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('linkmonitor')}
                className={`${activeTab === 'linkmonitor' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}`}
                data-testid="link-monitor-tab-btn"
              >
                <Link2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Monitor de Enlaces', 'Link Monitor')}</span>
              </Button>
              <Button
                variant={activeTab === 'ahrefsbatch' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('ahrefsbatch')}
                className={`${activeTab === 'ahrefsbatch' ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-orange-600 hover:bg-white font-medium'}`}
                data-testid="ahrefs-batch-tab-btn"
              >
                <BarChart3 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Ahrefs Batch', 'Ahrefs Batch')}</span>
              </Button>
              <Button
                variant={activeTab === 'ahrefsintegration' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('ahrefsintegration')}
                className={`${activeTab === 'ahrefsintegration' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}`}
                data-testid="ahrefs-integration-tab-btn"
              >
                <Database className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Integrar Ahrefs', 'Ahrefs Integration')}</span>
              </Button>
              <Button
                variant={activeTab === 'ahrefsupdate' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('ahrefsupdate')}
                className={`${activeTab === 'ahrefsupdate' ? 'bg-green-600 hover:bg-green-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-green-600 hover:bg-white font-medium'}`}
                data-testid="ahrefs-update-tab-btn"
              >
                <TrendingUp className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Ahrefs API', 'Ahrefs API')}</span>
              </Button>
              <Button
                variant={activeTab === 'cleanurls' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('cleanurls')}
                className={`${activeTab === 'cleanurls' ? 'bg-red-600 hover:bg-red-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-red-600 hover:bg-white font-medium'}`}
                data-testid="clean-urls-tab-btn"
              >
                <Scissors className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Limpiar URLs', 'Clean URLs')}</span>
              </Button>
              <Button
                variant={activeTab === 'apitoexcel' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('apitoexcel')}
                className={`${activeTab === 'apitoexcel' ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-cyan-600 hover:bg-white font-medium'}`}
                data-testid="api-to-excel-tab-btn"
              >
                <Globe className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('API a Excel', 'API to Excel')}</span>
              </Button>
              <Button
                variant={activeTab === 'catalogapi' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('catalogapi')}
                className={`${activeTab === 'catalogapi' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-emerald-600 hover:bg-white font-medium'}`}
                data-testid="catalog-api-tab-btn"
              >
                <Key className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('API Catálogo', 'Catalog API')}</span>
              </Button>
              <Button
                variant={activeTab === 'apisync' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('apisync')}
                className={`${activeTab === 'apisync' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-violet-600 hover:bg-white font-medium'}`}
                data-testid="api-sync-tab-btn"
              >
                <RefreshCw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Sync APIs', 'API Sync')}</span>
              </Button>
              <Button
                variant={activeTab === 'mediasearch' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('mediasearch')}
                className={`${activeTab === 'mediasearch' ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-cyan-600 hover:bg-white font-medium'}`}
                data-testid="media-search-tab-btn"
              >
                <Search className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Buscar Medios', 'Media Search')}</span>
              </Button>
              <Button
                variant={activeTab === 'emailtool' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('emailtool')}
                className={`${activeTab === 'emailtool' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}`}
                data-testid="email-tool-tab-btn"
              >
                <Mail className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Enviar Email', 'Email Tool')}</span>
              </Button>
              <Button
                variant={activeTab === 'categoryclassifier' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('categoryclassifier')}
                className={`${activeTab === 'categoryclassifier' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-violet-600 hover:bg-white font-medium'}`}
                data-testid="category-classifier-tab-btn"
              >
                <Tags className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Clasificar Categorías', 'Classify Categories')}</span>
              </Button>
              <Button
                variant={activeTab === 'seoprospector' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('seoprospector')}
                className={`${activeTab === 'seoprospector' ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-teal-600 hover:bg-white font-medium'}`}
                data-testid="seo-prospector-tab-btn"
              >
                <Search className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('SEO Prospector', 'SEO Prospector')}</span>
              </Button>
              <Button
                variant={activeTab === 'healthcheck' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('healthcheck')}
                className={`${activeTab === 'healthcheck' ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-emerald-600 hover:bg-white font-medium'}`}
                data-testid="health-check-tab-btn"
              >
                <ShieldCheck className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Health Check', 'Health Check')}</span>
              </Button>
              <Button
                variant={activeTab === 'bottester' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('bottester')}
                className={`${activeTab === 'bottester' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}`}
                data-testid="bot-tester-tab-btn"
              >
                <Bot className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Bot Tester</span>
              </Button>
            </div>
          </div>

          {/* Sección ADMINISTRACIÓN */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Settings className="h-3 w-3" />
              Administración
            </h3>
            <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl">
              <Button
                variant={activeTab === 'expenses' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('expenses')}
                className={activeTab === 'expenses' ? 'bg-red-600 hover:bg-red-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-red-600 hover:bg-white font-medium'}
              >
                <Receipt className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Gastos', 'Expenses')}</span>
              </Button>
              <Button
                variant={activeTab === 'accounting' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('accounting')}
                className={activeTab === 'accounting' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-blue-600 hover:bg-white font-medium'}
              >
                <Wallet className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Contabilidad', 'Accounting')}</span>
              </Button>
              <Button
                variant={activeTab === 'reminders' ? 'default' : 'ghost'}
                onClick={() => { setActiveTab('reminders'); fetchReminderData(); }}
                className={`${activeTab === 'reminders' ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-teal-600 hover:bg-white font-medium'}`}
              >
                <Bell className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Recordatorios', 'Reminders')}</span>
              </Button>
              <Button
                variant={activeTab === 'timetracking' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('timetracking')}
                className={`${activeTab === 'timetracking' ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-indigo-600 hover:bg-white font-medium'}`}
              >
                <Clock className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Fichaje', 'Time Tracking')}</span>
              </Button>
              <Button
                variant={activeTab === 'chatbot' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('chatbot')}
                className={`${activeTab === 'chatbot' ? 'bg-green-600 hover:bg-green-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-green-600 hover:bg-white font-medium'}`}
              >
                <MessageCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{tx('Chatbot', 'Chatbot')}</span>
              </Button>
              {user?.email?.toLowerCase() === 'soporte@purolink.com' && (
                <Button
                  variant={activeTab === 'sitecontent' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('sitecontent')}
                  className={`${activeTab === 'sitecontent' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md font-medium' : 'text-slate-600 hover:text-purple-600 hover:bg-white font-medium'}`}
                >
                  <FileText className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{tx('Web', 'Web')}</span>
                </Button>
              )}
            </div>
          </div>

          {/* Stats Tab - Refactored to StatsTab component */}
          <TabsContent value="stats">
            <StatsTab stats={stats} texts={texts} />
          </TabsContent>

          {/* Users Tab - Refactored to UsersTab component */}
          <TabsContent value="users">
            <UsersTab 
              users={users} 
              onRefresh={fetchData}
              texts={texts}
            />
          </TabsContent>

          {/* Consultas Tab - Refactored to ConsultasTab component */}
          <TabsContent value="consultas">
            <ConsultasTab 
              users={users}
              productEmails={productEmails}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ProductsTab onProductEdit={openProductDialog} />
          </TabsContent>

          {/* CRM Tab */}
          {/* Import Tab - Refactored to ImportTab component */}
          <TabsContent value="import">
            <ImportTab stats={stats} texts={texts} onRefresh={fetchData} />
          </TabsContent>

          {/* Wallet/Recharges Tab - Refactored to WalletTab component */}
          <TabsContent value="wallet">
            <WalletTab onPendingCountChange={setPendingRechargesCount} />
          </TabsContent>

          {/* Orders Tab - Separate from CRM */}
          <TabsContent value="orders">
            <CRMTab key={`orders-tab-${ordersTabKey}`} language={language} defaultView="orders" onOrdersChange={fetchPendingOrdersCount} />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <CRMTab language={language} defaultView="customers" />
          </TabsContent>

          {/* Writers Tab */}
          <TabsContent value="writers">
            <CRMTab language={language} defaultView="writers" />
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <CRMTab language={language} defaultView="expenses" />
          </TabsContent>

          {/* Accounting Tab */}
          <TabsContent value="accounting">
            <CRMTab language={language} defaultView="accounting" />
          </TabsContent>

          {/* Categories Tab */}
          {/* Categories Tab - Refactored to CategoriesTab component */}
          <TabsContent value="categories">
            <CategoriesTab token={token} />
          </TabsContent>

          {/* Coupons Tab - Refactored to CouponsTab component */}
          <TabsContent value="coupons">
            <CouponsTab />
          </TabsContent>

          {/* Reminders Tab - Refactored to RemindersTab component */}
          <TabsContent value="reminders">
            <RemindersTab />
          </TabsContent>

          {/* Time Tracking Tab - Para todos, pero con restricciones de edición */}
          <TabsContent value="timetracking">
            <TimeTrackingTab isSuperadmin={isSuperadmin} currentUser={user} />
          </TabsContent>

          {/* Chatbot Tab - Para todos los admins/trabajadores */}
          <TabsContent value="chatbot">
            <ChatbotTab token={token} />
          </TabsContent>

          {/* Site Content Tab - Solo para superadmin */}
          {user?.email?.toLowerCase() === 'soporte@purolink.com' && (
            <TabsContent value="sitecontent">
              <SiteContentTab />
            </TabsContent>
          )}

          {/* Excel Cleaner Tab - Para todos los admins/trabajadores */}
          <TabsContent value="excelcleaner" data-testid="excel-cleaner-tab-content">
            <ExcelCleanerTab />
          </TabsContent>

          {/* Invoice Classifier Tab - Clasificador de Facturas PDF */}
          <TabsContent value="invoiceclassifier" data-testid="invoice-classifier-tab-content">
            <InvoiceClassifierTab token={token} />
          </TabsContent>

          {/* PDF Filter Tab - Filtrador de PDFs por país */}
          <TabsContent value="pdffilter" data-testid="pdf-filter-tab-content">
            <PdfFilterTab token={token} />
          </TabsContent>

          {/* Client Catalog Tab - Para superadmin y trabajadores */}
          <TabsContent value="clientcatalog" data-testid="client-catalog-tab-content">
            <ClientCatalogTab />
          </TabsContent>

          {/* URL Checker Tab - Para superadmin y trabajadores */}
          <TabsContent value="urlchecker" data-testid="url-checker-tab-content">
            <UrlCheckerTab />
          </TabsContent>

          {/* Compare Tool Tab - Para superadmin y trabajadores */}
          <TabsContent value="comparetool" data-testid="compare-tool-tab-content">
            <CompareToolTab />
          </TabsContent>

          {/* Link Monitor Tab - Para superadmin y trabajadores */}
          <TabsContent value="linkmonitor" data-testid="link-monitor-tab-content">
            <LinkMonitorTab />
          </TabsContent>

          {/* Ahrefs Batch Tab - Para superadmin y trabajadores */}
          <TabsContent value="ahrefsbatch" data-testid="ahrefs-batch-tab-content">
            <AhrefsBatchTab />
          </TabsContent>

          {/* Ahrefs Integration Tab - Para superadmin y trabajadores */}
          <TabsContent value="ahrefsintegration" data-testid="ahrefs-integration-tab-content">
            <AhrefsIntegrationTab />
          </TabsContent>

          {/* Ahrefs API Update Tab - Para superadmin */}
          <TabsContent value="ahrefsupdate" data-testid="ahrefs-update-tab-content">
            <AhrefsUpdateTab language={language} />
          </TabsContent>

          {/* Clean URLs Tab - Para superadmin y trabajadores */}
          <TabsContent value="cleanurls" data-testid="clean-urls-tab-content">
            <CleanUrlsTab />
          </TabsContent>

          {/* API to Excel Tab - Para superadmin y trabajadores */}
          <TabsContent value="apitoexcel" data-testid="api-to-excel-tab-content">
            <ApiToExcelTab />
          </TabsContent>

          {/* Catalog API Tab - Para superadmin y trabajadores */}
          <TabsContent value="catalogapi" data-testid="catalog-api-tab-content">
            <CatalogApiTab />
          </TabsContent>

          {/* API Sync Tab - Sincronización de APIs de proveedores */}
          <TabsContent value="apisync" data-testid="api-sync-tab-content">
            <APISyncTab />
          </TabsContent>

          {/* Media Search Tab - Búsqueda masiva de medios */}
          <TabsContent value="mediasearch" data-testid="media-search-tab-content">
            <MediaSearchTab />
          </TabsContent>

          {/* Email Tool Tab - Herramienta de envío de emails */}
          <TabsContent value="emailtool" data-testid="email-tool-tab-content">
            <EmailToolTab />
          </TabsContent>

          {/* Category Classifier Tab */}
          <TabsContent value="categoryclassifier" data-testid="category-classifier-tab-content">
            <CategoryClassifierTab token={token} />
          </TabsContent>

          {/* SEO Prospector Tab - Buscar emails y enviar campañas */}
          <TabsContent value="seoprospector" data-testid="seo-prospector-tab-content">
            <SEOProspectorTab />
          </TabsContent>

          <TabsContent value="healthcheck" data-testid="health-check-tab-content">
            <HealthCheckTab language={language} />
          </TabsContent>

          <TabsContent value="bottester" data-testid="bot-tester-tab-content">
            <BotTesterTab />
          </TabsContent>
        </Tabs>

        {/* Coupon Dialog */}
        <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingCoupon ? (tx('Editar Cupón', 'Edit Coupon')) : (tx('Nuevo Cupón', 'New Coupon'))}
              </DialogTitle>
              <DialogDescription>
                {tx('Configura los detalles del cupón de descuento', 'Configure the discount coupon details')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{tx('Código del cupón', 'Coupon code')}</Label>
                <Input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="BIENVENIDO10"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  {tx('Este código lo usarán los clientes para aplicar el descuento', 'Customers will use this code to apply the discount')}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>{tx('Descripción (opcional)', 'Description (optional)')}</Label>
                <Input
                  value={couponForm.description}
                  onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                  placeholder={tx('Cupón de bienvenida para nuevos usuarios', 'Welcome coupon for new users')}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{tx('Tipo de descuento', 'Discount type')}</Label>
                  <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">{tx('Porcentaje (%)', 'Percentage (%)')}</SelectItem>
                      <SelectItem value="fixed">{tx('Cantidad fija (€)', 'Fixed amount (€)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{tx('Valor del descuento', 'Discount value')}</Label>
                  <Input
                    type="number"
                    min="0"
                    step={couponForm.discount_type === 'percentage' ? '1' : '0.01'}
                    value={couponForm.discount_value}
                    onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                    placeholder={couponForm.discount_type === 'percentage' ? '10' : '5.00'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{tx('Compra mínima (€)', 'Min. purchase (€)')}</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={couponForm.min_purchase}
                    onChange={(e) => setCouponForm({ ...couponForm, min_purchase: e.target.value })}
                    placeholder="0"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{tx('Máx. usos (0=∞)', 'Max uses (0=∞)')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={couponForm.max_uses}
                    onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{tx('Fecha de expiración (opcional)', 'Expiration date (optional)')}</Label>
                <Input
                  type="date"
                  value={couponForm.expires_at ? couponForm.expires_at.split('T')[0] : ''}
                  onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>
                {tx('Cancelar', 'Cancel')}
              </Button>
              <Button onClick={handleSaveCoupon}>
                {editingCoupon ? (tx('Guardar Cambios', 'Save Changes')) : (tx('Crear Cupón', 'Create Coupon'))}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Product Dialog */}
        <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingProduct ? texts.editProduct : texts.addProduct}</DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>{texts.website}</Label>
                <Input
                  value={productForm.website}
                  onChange={(e) => {
                    // Clean URL: remove protocol and trailing slash
                    let url = e.target.value
                      .replace(/^https?:\/\//i, '')
                      .replace(/^www\./i, '')
                      .replace(/\/+$/, '')
                      .trim();
                    setProductForm({ ...productForm, website: url });
                  }}
                  placeholder="ejemplo.com"
                />
              </div>

              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={productForm.web_email}
                  onChange={(e) => setProductForm({ ...productForm, web_email: e.target.value })}
                  placeholder="contacto@example.com"
                />
              </div>

              <div className="col-span-2">
                <Label>2nd Mail (Email secundario)</Label>
                <Input
                  type="email"
                  value={productForm.second_email}
                  onChange={(e) => setProductForm({ ...productForm, second_email: e.target.value })}
                  placeholder="secundario@example.com"
                />
              </div>

              <div className="col-span-2">
                <Label>{tx('Sensible', 'Sensitive')}</Label>
                <Select 
                  value={productForm.casino ? 'Sí' : 'No'} 
                  onValueChange={(v) => setProductForm({ ...productForm, casino: v === 'Sí' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Sí">Sí</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {tx(
                    'Marcar "Sí" si el producto acepta contenido sensible (casino, apuestas, crypto, adultos, etc.)',
                    'Mark "Yes" if the product accepts sensitive content (casino, gambling, crypto, adult, etc.)'
                  )}
                </p>
              </div>

              <div className="col-span-2">
                <Label>Tag / Etiqueta</Label>
                <Input
                  value={productForm.tag_label}
                  onChange={(e) => setProductForm({ ...productForm, tag_label: e.target.value })}
                  placeholder={tx('Ej: DESTACADO, NUEVO, PREMIUM...', 'E.g.: FEATURED, NEW, PREMIUM...')}
                />
              </div>

              <div>
                <Label>{texts.country}</Label>
                <SearchableSelect
                  value={productForm.country}
                  onValueChange={(v) => setProductForm({ ...productForm, country: v })}
                  options={PRODUCT_COUNTRIES}
                  placeholder={tx('Seleccionar país', 'Select country')}
                  searchPlaceholder={tx('Buscar país...', 'Search country...')}
                  emptyText={tx('Sin resultados', 'No results')}
                  testId="product-country-select"
                />
              </div>

              <div className="col-span-2">
                <Label>{texts.category}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal h-auto min-h-[40px] py-2">
                      <span className="text-muted-foreground">
                        {tx('Añadir categorías...', 'Add categories...')}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                    <div className="p-2 border-b">
                      <Input
                        autoFocus
                        placeholder={tx('Buscar categoría...', 'Search category...')}
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        className="h-8"
                        data-testid="product-category-search"
                      />
                    </div>
                    <ScrollArea className="h-[200px]" onWheel={(e) => e.stopPropagation()}>
                      <div className="p-2 space-y-1">
                        {customCategories
                          .filter((cat) => cat.toLowerCase().startsWith(categorySearch.toLowerCase()))
                          .map((cat) => {
                            const categories = productForm.category ? productForm.category.split(', ').filter(c => c) : [];
                            const isSelected = categories.includes(cat);
                            return (
                              <label
                                key={cat}
                                className={`flex items-center gap-2 p-1.5 rounded cursor-pointer ${isSelected ? 'bg-muted opacity-50' : 'hover:bg-muted'}`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  disabled={isSelected}
                                onCheckedChange={(checked) => {
                                  if (checked && !isSelected) {
                                    const newCategories = [...categories, cat];
                                    setProductForm({ ...productForm, category: newCategories.join(', ') });
                                  }
                                }}
                              />
                              <span className="text-sm">{cat}</span>
                            </label>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
                {/* Selected categories display */}
                {productForm.category && productForm.category.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {productForm.category.split(', ').filter(c => c).map((cat) => (
                      <Badge key={cat} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                        {cat}
                        <button
                          type="button"
                          onClick={() => {
                            const newCategories = productForm.category.split(', ').filter(c => c && c !== cat);
                            setProductForm({ ...productForm, category: newCategories.join(', ') });
                          }}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label>{texts.price}</Label>
                <Input
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>{tx('Precio Compra', 'Purchase Price')}</Label>
                <Input
                  type="number"
                  value={productForm.pc}
                  onChange={(e) => setProductForm({ ...productForm, pc: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>{texts.priceSensitive}</Label>
                <Input
                  type="number"
                  value={productForm.price_sensitive}
                  onChange={(e) => setProductForm({ ...productForm, price_sensitive: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>{tx('Precio Compra Sensible', 'Sensitive Purchase Price')}</Label>
                <Input
                  type="number"
                  value={productForm.premium_purchase_price}
                  onChange={(e) => setProductForm({ ...productForm, premium_purchase_price: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="col-span-2">
                <Label>{tx('Más Info PC (Info adicional precio compra)', 'More Info PC')}</Label>
                <Input
                  value={productForm.mas_info_pc}
                  onChange={(e) => setProductForm({ ...productForm, mas_info_pc: e.target.value })}
                  placeholder={tx('Información adicional sobre precio de compra...', 'Additional purchase price info...')}
                />
              </div>

              <div className="col-span-2">
                <Label className="text-red-600">{tx('Motivo Eliminación', 'Removal Reason')}</Label>
                <Input
                  value={productForm.motivo_eliminacion}
                  onChange={(e) => setProductForm({ ...productForm, motivo_eliminacion: e.target.value })}
                  placeholder={tx('Si no disponible, indicar motivo...', 'If unavailable, indicate reason...')}
                  className="border-red-200 focus:border-red-400"
                />
              </div>

              <div>
                <Label>{texts.dr}</Label>
                <Input
                  type="number"
                  value={productForm.dr}
                  onChange={(e) => setProductForm({ ...productForm, dr: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>{texts.traffic}</Label>
                <Input
                  type="number"
                  value={productForm.organic_traffic}
                  onChange={(e) => setProductForm({ ...productForm, organic_traffic: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div>
                <Label>{texts.languageField}</Label>
                <SearchableSelect
                  value={productForm.language}
                  onValueChange={(v) => setProductForm({ ...productForm, language: v })}
                  options={PRODUCT_LANGUAGES}
                  placeholder={tx('Seleccionar idioma', 'Select language')}
                  searchPlaceholder={tx('Buscar idioma...', 'Search language...')}
                  emptyText={tx('Sin resultados', 'No results')}
                  testId="product-language-select"
                />
              </div>

              <div>
                <Label>{texts.siteType}</Label>
                <SearchableSelect
                  value={productForm.site_type || 'Web/Blog'}
                  onValueChange={(v) => setProductForm({ ...productForm, site_type: v })}
                  options={SITE_TYPES}
                  placeholder="Web/Blog"
                  searchPlaceholder={tx('Buscar...', 'Search...')}
                  emptyText={tx('Sin resultados', 'No results')}
                  testId="product-sitetype-select"
                />
              </div>

              <div>
                <Label>{texts.linkType}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal">
                      {productForm.link_type && productForm.link_type.length > 0
                        ? productForm.link_type
                        : (tx('Seleccionar tipos', 'Select types'))}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-2" align="start">
                    <div className="space-y-1">
                      {LINK_TYPES.map((type) => {
                        const types = productForm.link_type ? productForm.link_type.split(', ').filter(t => t) : [];
                        return (
                          <label key={type} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer">
                            <Checkbox
                              checked={types.includes(type)}
                              onCheckedChange={(checked) => {
                                const newTypes = checked
                                  ? [...types, type]
                                  : types.filter(t => t !== type);
                                setProductForm({ ...productForm, link_type: newTypes.join(', ') });
                              }}
                            />
                            <span className="text-sm">{type}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>{texts.linkDuration}</Label>
                <SearchableSelect
                  value={productForm.link_duration}
                  onValueChange={(v) => setProductForm({ ...productForm, link_duration: v })}
                  options={LINK_DURATIONS}
                  placeholder={tx('Seleccionar duración', 'Select duration')}
                  searchPlaceholder={tx('Buscar...', 'Search...')}
                  emptyText={tx('Sin resultados', 'No results')}
                  testId="product-linkduration-select"
                />
              </div>

              <div>
                <Label>{texts.adLabel}</Label>
                <Select value={productForm.ad_label} onValueChange={(v) => setProductForm({ ...productForm, ad_label: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder={tx('Seleccionar', 'Select')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sí">{tx('Sí', 'Yes')}</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>{texts.moreInfo}</Label>
                <Textarea
                  value={productForm.more_info}
                  onChange={(e) => setProductForm({ ...productForm, more_info: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.available}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, available: checked })}
                />
                <Label>{texts.available}</Label>
              </div>
            </div>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4 bg-background sticky bottom-0">
              <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
                {texts.cancel}
              </Button>
              <Button onClick={handleProductSave}>{texts.save}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Create User Dialog */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{texts.createUser}</DialogTitle>
              <DialogDescription>
                {tx('Crea un nuevo usuario para acceder al sistema', 'Create a new user to access the system')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="userName">{texts.name}</Label>
                <Input
                  id="userName"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder={tx('Nombre completo', 'Full name')}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userEmail">{texts.email}</Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userPassword">{texts.password}</Label>
                <Input
                  id="userPassword"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="userIsAdmin"
                  checked={userForm.is_admin}
                  onCheckedChange={(checked) => setUserForm({ ...userForm, is_admin: checked })}
                />
                <Label htmlFor="userIsAdmin">{texts.isAdmin}</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                {texts.cancel}
              </Button>
              <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-emerald-600 hover:bg-emerald-700">
                {creatingUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {texts.createUser}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete All Confirmation Dialog */}
        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {texts.confirmDeleteAllTitle}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {texts.confirmDeleteAllDesc}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label className="text-sm font-medium">{texts.typeToConfirm}</Label>
              <Input
                className="mt-2"
                placeholder={texts.deleteWord}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setDeleteConfirmText(''); }}>
                {texts.cancel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllProducts}
                disabled={deleteConfirmText !== texts.deleteWord}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {texts.deleteAll}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* User Import Results Dialog */}
        <Dialog open={showUserImportDialog} onOpenChange={setShowUserImportDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {tx('Importación Completada', 'Import Completed')}
              </DialogTitle>
            </DialogHeader>
            {userImportResult && (
              <div className="space-y-3 py-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-green-700 font-medium">{tx('Usuarios creados', 'Users created')}</span>
                  <Badge className="bg-green-600">{userImportResult.created || 0}</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-blue-700 font-medium">{tx('Usuarios actualizados', 'Users updated')}</span>
                  <Badge className="bg-blue-600">{userImportResult.updated || 0}</Badge>
                </div>
                {userImportResult.skipped > 0 && (
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-700 font-medium">{tx('Usuarios omitidos', 'Users skipped')}</span>
                    <Badge className="bg-yellow-600">{userImportResult.skipped}</Badge>
                  </div>
                )}
                {userImportResult.errors && userImportResult.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg">
                    <p className="text-red-700 font-medium mb-2">{tx('Errores:', 'Errors:')}</p>
                    <ul className="text-sm text-red-600 list-disc list-inside">
                      {userImportResult.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {userImportResult.errors.length > 5 && (
                        <li>... {tx('y', 'and')} {userImportResult.errors.length - 5} {tx('más', 'more')}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setShowUserImportDialog(false)}>
                {tx('Cerrar', 'Close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Wallet Adjustment Dialog */}
        <Dialog open={walletAdjustDialog} onOpenChange={setWalletAdjustDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-green-600" />
                {tx('Ajustar Saldo', 'Adjust Balance')}
              </DialogTitle>
              <DialogDescription>
                {walletAdjustUser && (
                  <span>
                    {walletAdjustUser.name} ({walletAdjustUser.email}) - 
                    {tx(' Saldo actual: ', ' Current balance: ')}
                    <strong className="text-green-600">€{(walletAdjustUser.wallet_balance || 0).toFixed(2)}</strong>
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Type selection */}
              <div className="space-y-2">
                <Label>{tx('Tipo de ajuste', 'Adjustment type')}</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={walletAdjustType === 'add' ? 'default' : 'outline'}
                    className={walletAdjustType === 'add' ? 'flex-1 bg-green-600 hover:bg-green-700' : 'flex-1'}
                    onClick={() => setWalletAdjustType('add')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {tx('Añadir saldo', 'Add balance')}
                  </Button>
                  <Button
                    type="button"
                    variant={walletAdjustType === 'subtract' ? 'destructive' : 'outline'}
                    className="flex-1"
                    onClick={() => setWalletAdjustType('subtract')}
                  >
                    <Minus className="h-4 w-4 mr-2" />
                    {tx('Quitar saldo', 'Subtract balance')}
                  </Button>
                </div>
              </div>
              
              {/* Amount */}
              <div className="space-y-2">
                <Label htmlFor="wallet-amount">{tx('Importe (€)', 'Amount (€)')}</Label>
                <Input
                  id="wallet-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={walletAdjustAmount}
                  onChange={(e) => setWalletAdjustAmount(e.target.value)}
                />
              </div>
              
              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="wallet-reason">
                  {tx('Motivo (se enviará al cliente por email)', 'Reason (will be sent to customer by email)')}
                </Label>
                <textarea
                  id="wallet-reason"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={tx('Ej: Compensación por retraso en el pedido #123', 'E.g.: Compensation for delay in order #123')}
                  value={walletAdjustReason}
                  onChange={(e) => setWalletAdjustReason(e.target.value)}
                />
              </div>
              
              {/* Preview */}
              {walletAdjustAmount && parseFloat(walletAdjustAmount) > 0 && walletAdjustUser && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm text-muted-foreground mb-1">{tx('Nuevo saldo:', 'New balance:')}</p>
                  <p className={`text-xl font-bold ${walletAdjustType === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                    €{(walletAdjustType === 'add' 
                      ? (walletAdjustUser.wallet_balance || 0) + parseFloat(walletAdjustAmount)
                      : (walletAdjustUser.wallet_balance || 0) - parseFloat(walletAdjustAmount)
                    ).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setWalletAdjustDialog(false)}>
                {tx('Cancelar', 'Cancel')}
              </Button>
              <Button 
                onClick={handleWalletAdjustment}
                disabled={adjustingWallet || !walletAdjustAmount || !walletAdjustReason.trim()}
                className={walletAdjustType === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {adjustingWallet ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {tx('Procesando...', 'Processing...')}
                  </>
                ) : (
                  <>
                    {walletAdjustType === 'add' ? <Plus className="h-4 w-4 mr-2" /> : <Minus className="h-4 w-4 mr-2" />}
                    {tx('Confirmar y Notificar', 'Confirm & Notify')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminPage;
